/**
 * User state — settings + profile + consents (Sprint 1.9 shell utilisateur).
 *
 * Abstraction localStorage-first. Sprint 2.1 swappera l'implémentation contre
 * Neon DB sans changer les consumers (pages /settings, /profile, /privacy).
 *
 * Stockage : localStorage clé `labrain.user-state` avec versioning forward-compatible
 * `{ v: 1, ... }`. Fallback in-memory si localStorage indisponible (Safari private mode).
 *
 * Hors scope : auth (handled lib/auth.ts), game progression (lib/game-progression.ts),
 * onboarding draft (onboardingStore.ts). Cette lib gère uniquement la couche compte/réglages.
 */
import { i18n } from './i18n';
import { SyncEngine } from './sync';
export const USER_STATE_KEY = 'labrain.user-state';
const STORAGE_VERSION = 1;
export const AVATAR_EMOJIS = [
    '🦋',
    '🌿',
    '🪶',
    '🔥',
    '💧',
    '⚡',
    '🌙',
    '🪞',
    '🌳',
    '🐾',
    '☀️',
    '🪨',
];
const DEFAULT_SETTINGS = {
    language: 'fr',
    enableSound: true,
    enableHaptics: true,
    enableTelemetry: true,
    themePreference: 'light',
};
function defaultProfile() {
    return {
        displayName: null,
        avatarEmoji: '🦋',
        bio: null,
        createdAt: new Date().toISOString(),
    };
}
function defaultState() {
    return {
        settings: { ...DEFAULT_SETTINGS },
        profile: defaultProfile(),
        consents: [],
        ageBand: null,
    };
}
let memoryStore = null;
let storageWarned = false;
let corruptionLogged = false;
function getStorage() {
    if (typeof window === 'undefined')
        return null;
    try {
        const ls = window.localStorage;
        const probeKey = `${USER_STATE_KEY}__probe__`;
        ls.setItem(probeKey, '1');
        ls.removeItem(probeKey);
        return ls;
    }
    catch {
        if (!storageWarned) {
            // eslint-disable-next-line no-console
            console.warn('[user-state] localStorage unavailable — falling back to in-memory store');
            storageWarned = true;
        }
        return null;
    }
}
function logCorruption() {
    if (corruptionLogged)
        return;
    corruptionLogged = true;
    // eslint-disable-next-line no-console
    console.warn('[user-state] data_corrupted_reset — JSON parse failed, defaults restored');
}
function isLocale(value) {
    return value === 'fr' || value === 'en';
}
function isAvatar(value) {
    return typeof value === 'string' && AVATAR_EMOJIS.includes(value);
}
function isAgeBand(value) {
    return (value === '18_24' ||
        value === '25_34' ||
        value === '35_44' ||
        value === '45_54' ||
        value === '55_plus');
}
function isConsentType(value) {
    return (value === 'health_data' ||
        value === 'research' ||
        value === 'marketing' ||
        value === 'ai_act_disclosure');
}
function sanitizeSettings(raw) {
    const r = (raw ?? {});
    return {
        language: isLocale(r.language) ? r.language : DEFAULT_SETTINGS.language,
        enableSound: typeof r.enableSound === 'boolean' ? r.enableSound : DEFAULT_SETTINGS.enableSound,
        enableHaptics: typeof r.enableHaptics === 'boolean' ? r.enableHaptics : DEFAULT_SETTINGS.enableHaptics,
        enableTelemetry: typeof r.enableTelemetry === 'boolean' ? r.enableTelemetry : DEFAULT_SETTINGS.enableTelemetry,
        themePreference: 'light',
    };
}
function sanitizeProfile(raw) {
    const r = (raw ?? {});
    const fallback = defaultProfile();
    return {
        displayName: typeof r.displayName === 'string' && r.displayName.length > 0
            ? r.displayName.slice(0, 32)
            : null,
        avatarEmoji: isAvatar(r.avatarEmoji) ? r.avatarEmoji : fallback.avatarEmoji,
        bio: typeof r.bio === 'string' && r.bio.length > 0
            ? r.bio.slice(0, 200)
            : null,
        createdAt: typeof r.createdAt === 'string' ? r.createdAt : fallback.createdAt,
    };
}
function sanitizeConsents(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw) {
        if (!item || typeof item !== 'object')
            continue;
        const c = item;
        if (!isConsentType(c.type))
            continue;
        if (typeof c.granted !== 'boolean')
            continue;
        if (typeof c.version !== 'string')
            continue;
        if (typeof c.ts !== 'string')
            continue;
        out.push({ type: c.type, granted: c.granted, version: c.version, ts: c.ts });
    }
    return out;
}
function readPersisted() {
    const storage = getStorage();
    if (!storage) {
        return memoryStore;
    }
    let raw;
    try {
        raw = storage.getItem(USER_STATE_KEY);
    }
    catch {
        return null;
    }
    if (!raw)
        return null;
    try {
        const parsed = JSON.parse(raw);
        if (parsed.v !== STORAGE_VERSION) {
            return null;
        }
        return {
            v: STORAGE_VERSION,
            settings: sanitizeSettings(parsed.settings),
            profile: sanitizeProfile(parsed.profile),
            consents: sanitizeConsents(parsed.consents),
            ageBand: isAgeBand(parsed.ageBand) ? parsed.ageBand : null,
        };
    }
    catch {
        logCorruption();
        return null;
    }
}
function writePersisted(state) {
    const persisted = {
        v: STORAGE_VERSION,
        settings: sanitizeSettings(state.settings),
        profile: sanitizeProfile(state.profile),
        consents: sanitizeConsents(state.consents),
        ageBand: state.ageBand,
    };
    const storage = getStorage();
    if (!storage) {
        memoryStore = persisted;
        return;
    }
    try {
        storage.setItem(USER_STATE_KEY, JSON.stringify(persisted));
        memoryStore = persisted;
    }
    catch {
        memoryStore = persisted;
    }
}
function toView(persisted) {
    return {
        settings: persisted.settings,
        profile: persisted.profile,
        consents: [...persisted.consents],
        ageBand: persisted.ageBand,
    };
}
export function loadUserState() {
    const persisted = readPersisted();
    if (!persisted)
        return defaultState();
    return toView(persisted);
}
export function saveUserSettings(patch) {
    const current = loadUserState();
    const next = {
        ...current,
        settings: sanitizeSettings({ ...current.settings, ...patch }),
    };
    writePersisted(next);
    if (patch.language && patch.language !== i18n.language) {
        void i18n.changeLanguage(patch.language);
    }
    if (patch.language) {
        SyncEngine.pushMe({ locale: next.settings.language });
    }
    return next;
}
export function saveUserProfile(patch) {
    const current = loadUserState();
    const next = {
        ...current,
        profile: sanitizeProfile({ ...current.profile, ...patch }),
    };
    writePersisted(next);
    SyncEngine.pushMe({
        displayName: next.profile.displayName,
        avatarEmoji: next.profile.avatarEmoji,
        bio: next.profile.bio,
    });
    return next;
}
export function recordConsent(consent) {
    const current = loadUserState();
    const filtered = current.consents.filter((c) => !(c.type === consent.type && c.version === consent.version));
    const next = {
        ...current,
        consents: [...filtered, { ...consent }],
    };
    writePersisted(next);
    void SyncEngine.pushConsent(consent);
    return next;
}
export function revokeConsent(type) {
    const current = loadUserState();
    const last = [...current.consents].reverse().find((c) => c.type === type);
    if (!last)
        return current;
    const revocation = {
        type,
        granted: false,
        version: last.version,
        ts: new Date().toISOString(),
    };
    const next = {
        ...current,
        consents: [...current.consents, revocation],
    };
    writePersisted(next);
    void SyncEngine.pushConsent(revocation);
    return next;
}
export function setAgeBand(band) {
    const current = loadUserState();
    const next = { ...current, ageBand: band };
    writePersisted(next);
    return next;
}
/**
 * Merge a remote MeProfile + consents into localStorage. Called from useAuth on session refresh.
 * DB is canonical for profile + locale + consents (server timestamps trump client).
 */
export function applySyncedState(remote) {
    const current = loadUserState();
    const merged = {
        settings: {
            ...current.settings,
            language: isLocale(remote.me.locale) ? remote.me.locale : current.settings.language,
        },
        profile: {
            displayName: typeof remote.me.displayName === 'string' && remote.me.displayName.length > 0
                ? remote.me.displayName.slice(0, 32)
                : null,
            avatarEmoji: isAvatar(remote.me.avatarEmoji)
                ? remote.me.avatarEmoji
                : current.profile.avatarEmoji,
            bio: typeof remote.me.bio === 'string' && remote.me.bio.length > 0
                ? remote.me.bio.slice(0, 200)
                : null,
            createdAt: typeof remote.me.createdAt === 'string' ? remote.me.createdAt : current.profile.createdAt,
        },
        consents: remote.consents.map((c) => ({
            type: c.consentType,
            granted: c.granted,
            version: c.aiActDisclosureVersion,
            ts: c.timestamp,
        })),
        ageBand: remote.me.ageBand,
    };
    writePersisted(merged);
    if (merged.settings.language !== i18n.language) {
        void i18n.changeLanguage(merged.settings.language);
    }
    return merged;
}
/**
 * Efface localStorage `labrain.*` (settings, profile, consents, progression, locale, onboarding).
 * Sprint 2.1 ajoutera DELETE backend Neon DB.
 */
export function deleteAllUserData() {
    memoryStore = null;
    const storage = getStorage();
    if (!storage)
        return;
    try {
        const keys = [];
        for (let i = 0; i < storage.length; i += 1) {
            const k = storage.key(i);
            if (k && k.startsWith('labrain.'))
                keys.push(k);
        }
        for (const k of keys) {
            try {
                storage.removeItem(k);
            }
            catch {
                /* ignored */
            }
        }
    }
    catch {
        /* ignored */
    }
}
/** Helper test : reset module-level memory + warning flags. */
export function __resetUserStateForTests() {
    memoryStore = null;
    storageWarned = false;
    corruptionLogged = false;
}
//# sourceMappingURL=user-state.js.map